// A small history router: the app has five routes and no need for a library.
import { useEffect, useState } from "react";

export type Route =
  | { name: "inbox" }
  | { name: "card"; requestId: string }
  | { name: "recent" }
  | { name: "connections" }
  | { name: "message" }
  | { name: "pair"; hash: string }
  /** A link of ours that can't be read, such as /card/%E0: "That link didn't work" (security review 2, S6). */
  | { name: "broken"; path: string }
  | { name: "unknown"; path: string };

export function parseRoute(pathname: string, hash: string): Route {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return { name: "inbox" };
  if (path === "/recent") return { name: "recent" };
  if (path === "/connections") return { name: "connections" };
  if (path === "/message") return { name: "message" };
  if (path === "/pair") return { name: "pair", hash };
  const card = /^\/card\/([^/]+)$/.exec(path);
  if (card?.[1]) {
    try {
      return { name: "card", requestId: decodeURIComponent(card[1]) };
    } catch {
      return { name: "broken", path };
    }
  }
  return { name: "unknown", path };
}

export function pathFor(route: Route): string {
  switch (route.name) {
    case "inbox":
      return "/";
    case "card":
      return `/card/${encodeURIComponent(route.requestId)}`;
    case "recent":
      return "/recent";
    case "connections":
      return "/connections";
    case "message":
      return "/message";
    case "pair":
      return `/pair${route.hash}`;
    case "broken":
    case "unknown":
      return route.path;
  }
}

const listeners = new Set<() => void>();

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  if (typeof history === "undefined") return;
  const current = location.pathname + location.search + location.hash;
  if (current === path && !opts.replace) return;
  if (opts.replace) history.replaceState(null, "", path);
  else history.pushState(null, "", path);
  for (const fn of listeners) fn();
}

/** Rewrites the address bar without re-routing, so the screen stays put: for taking a secret out of the URL. */
export function rewriteUrl(path: string): void {
  if (typeof history !== "undefined") history.replaceState(history.state, "", path);
}

export function back(fallback = "/"): void {
  if (history.length > 1) history.back();
  else navigate(fallback, { replace: true });
}

export function currentRoute(): Route {
  if (typeof location === "undefined") return { name: "inbox" };
  return parseRoute(location.pathname, location.hash);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(currentRoute);
  useEffect(() => {
    const update = () => setRoute(currentRoute());
    listeners.add(update);
    window.addEventListener("popstate", update);
    return () => {
      listeners.delete(update);
      window.removeEventListener("popstate", update);
    };
  }, []);
  return route;
}

/** For <a> clicks: keep it a real link for accessibility, but route in-app. */
export function onLinkClick(e: { preventDefault(): void; metaKey?: boolean; ctrlKey?: boolean; button?: number }, href: string): void {
  if (e.metaKey || e.ctrlKey || (e.button !== undefined && e.button !== 0)) return;
  e.preventDefault();
  navigate(href);
}
